// biome-ignore-all lint/plugin/no-unlabeled-form-inputs: legacy unlabeled inputs, burn-down tracked in HTUI-392
import { Fragment, Ref, forwardRef, useEffect, useMemo, useRef } from "react";

import { Column, Row, Spinner, Text, TextInput, Box } from "@hightouch/htui";
import {
  ActionImpl,
  KBarAnimator,
  KBarPortal,
  KBarPositioner,
  KBarProvider,
  KBarResults,
  VisualState,
  useKBar,
  useMatches,
} from "kbar";

import { CommandBarProvider, useCommandBar } from "./context";

const animatorStyle = {
  maxWidth: "600px",
  width: "100%",
  borderRadius: "var(--chakra-radii-2xl)",
  overflow: "hidden",
};

/**
 * TODO:
 *  - Add loading indicator to workspace switcher
 */

const KBAR_LISTBOX = "kbar-listbox";
const getListboxItemId = (id: number) => `kbar-listbox-item-${id}`;

const Search = ({ defaultPlaceholder = undefined }) => {
  const { isFetching } = useCommandBar();
  const {
    query,
    search,
    actions,
    currentRootActionId,
    activeIndex,
    showing,
    options,
  } = useKBar((state) => ({
    search: state.searchQuery,
    currentRootActionId: state.currentRootActionId,
    actions: state.actions,
    activeIndex: state.activeIndex,
    showing: state.visualState === VisualState.showing,
  }));

  const ownRef = useRef<HTMLInputElement | null>(null);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (!ownRef.current) {
      return () => query.setSearch("");
    }

    query.setSearch("");
    ownRef.current.focus();

    return () => query.setSearch("");
  }, [currentRootActionId, query]);

  const placeholder = useMemo((): string => {
    const defaultText = defaultPlaceholder ?? "Type a command or search…";
    return currentRootActionId
      ? (actions[currentRootActionId]?.name ?? defaultText)
      : defaultText;
  }, [actions, currentRootActionId, defaultPlaceholder]);

  return (
    <Row
      align="center"
      px={4}
      py={2}
      sx={{
        "& > input": {
          border: "none",
          p: 0,
          background: "none",
          boxShadow: "none",
        },
      }}
    >
      <Box
        width="100%"
        sx={{
          "& input": {
            border: "none",
            outline: "none",
            boxShadow: "none",
            background: "transparent",
            width: "100%",
            padding: "0",
            margin: "0",
          },
          "& input:focus": {
            border: "none",
            outline: "none",
            boxShadow: "none",
            background: "transparent",
          },
          "& input:focus-visible": {
            border: "none",
            outline: "none",
            boxShadow: "none",
            background: "transparent",
          },
          "& input::placeholder": {
            color: "inherit",
            opacity: "0.6",
          },
        }}
      >
        <TextInput
          ref={(ref) => {
            if (!ref) {
              return;
            }

            ownRef.current = ref;
            query.inputRefSetter(ref);
          }}
          autoFocus
          aria-activedescendant={getListboxItemId(activeIndex)}
          aria-controls={KBAR_LISTBOX}
          aria-expanded={showing}
          placeholder={isFetching ? "Loading..." : placeholder}
          size="lg"
          width="100%"
          value={search}
          onChange={(event) => {
            query.setSearch(event.target.value);
            options?.callbacks?.onQueryChange?.(event.target.value);
          }}
          onKeyDown={(event) => {
            if (currentRootActionId && !search && event.key === "Backspace") {
              const parent = actions[currentRootActionId]?.parent;
              query.setCurrentRootAction(parent);
            }
          }}
        />
      </Box>
      {isFetching && <Spinner marginLeft="auto" />}
    </Row>
  );
};

const ResultItem = forwardRef(
  (
    {
      action,
      active,
      currentRootActionId,
    }: {
      action: ActionImpl;
      active: boolean;
      currentRootActionId: string;
    },
    ref: Ref<HTMLDivElement>,
  ) => {
    // TODO: Fix render

    const ancestors = useMemo(() => {
      if (!currentRootActionId) return action.ancestors;
      const index = action.ancestors.findIndex(
        (ancestor) => ancestor.id === currentRootActionId,
      );
      return action.ancestors.slice(index + 1);
    }, [action.ancestors, currentRootActionId]);

    return (
      <Row
        ref={ref}
        cursor="pointer"
        align="center"
        bg={active ? "primary.background" : "white"}
        borderRadius="lg"
        mx={1.5}
        py={4}
        px={3}
        transitionProperty="border-color, background-color"
        transitionDuration="fast"
        transitionTimingFunction="easeOutQuad"
      >
        <>
          <Row
            color={active ? "primary.base" : "text.secondary"}
            flex={1}
            align="center"
          >
            {action.icon && (
              <Row
                as={action.icon as any}
                boxSize={4}
                color="inherit"
                mr={2}
                transitionProperty="color"
                transitionDuration="fast"
                transitionTimingFunction="easeOutQuad"
              />
            )}
            {ancestors.length > 0 &&
              ancestors.map((ancestor) => (
                <Fragment key={ancestor.id}>
                  <Box as="span" opacity={0.8} marginRight={2}>
                    {ancestor.name}
                  </Box>
                  <Box as="span" opacity={0.8} marginRight={2}>
                    &rsaquo;
                  </Box>
                </Fragment>
              ))}

            <Column>
              <Box
                as={Text}
                color="inherit"
                transitionProperty="color"
                transitionDuration="fast"
                transitionTimingFunction="easeOutQuad"
              >
                {action.name}
              </Box>
              {action.subtitle && (
                <Text color="text.tertiary" size="sm">
                  {action.subtitle}
                </Text>
              )}
            </Column>
          </Row>

          {action.shortcut?.length ? (
            <Row gap={1}>
              {action.shortcut.map((sc) => (
                <kbd
                  key={sc}
                  style={{
                    padding: "2px 6px",
                    background: "rgba(0 0 0 / .1)",
                    borderRadius: "4px",
                    fontSize: 14,
                  }}
                >
                  {sc}
                </kbd>
              ))}
            </Row>
          ) : null}
        </>
      </Row>
    );
  },
);

ResultItem.displayName = "ResultItem";

function Results() {
  const { results, rootActionId } = useMatches();
  if (results?.length === 0) {
    return (
      <Column
        as={Text}
        alignItems="center"
        color="text.tertiary"
        h={12}
        justifyContent="center"
        m={4}
        textAlign="center"
      >
        No items match this search
      </Column>
    );
  }

  const filteredResults = results.filter((result) => {
    if (typeof result === "string") {
      return true;
    }

    // Exclude workspace switch options from root.
    if (
      rootActionId !== "workspace.switch" &&
      result.id.startsWith("workspace.switch.")
    ) {
      return false;
    }

    return true;
  });

  return (
    <KBarResults
      items={filteredResults}
      onRender={({ item, active }) =>
        typeof item === "string" ? (
          <Box
            as={Text}
            color="text.tertiary"
            display="inline-block"
            fontWeight="medium"
            size="sm"
            px={4}
            py={2}
            width="100%"
          >
            {item}
          </Box>
        ) : (
          <ResultItem
            action={item}
            active={active}
            currentRootActionId={rootActionId ?? ""}
          />
        )
      }
    />
  );
}

export const CommandBar = ({ children }) => {
  return (
    <CommandBarProvider>
      <KBarProvider actions={[]}>
        <KBarPortal>
          <Row
            position="fixed"
            width="100%"
            height="100%"
            top={0}
            left={0}
            bg="rgba(0, 0, 0, 0.4)"
            backdropFilter="blur(12px)"
            justifyContent="center"
            alignItems="center"
            boxShadow="lg"
            zIndex="popover"
          >
            <KBarPositioner>
              <KBarAnimator style={animatorStyle}>
                <Row
                  justifyContent="center"
                  alignItems="center"
                  overflow="hidden"
                >
                  <Column
                    borderRadius="2xl"
                    bg="white"
                    width="500px"
                    overflow="hidden"
                    maxHeight="100%"
                  >
                    <Search />
                    <Results />
                  </Column>
                </Row>
              </KBarAnimator>
            </KBarPositioner>
          </Row>
        </KBarPortal>
        {children}
      </KBarProvider>
    </CommandBarProvider>
  );
};
